import React, { useMemo, useState } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { describeAudience } from "./noticeHelpers";

const MODES = [
  { value: "all", label: "Everyone", icon: "users" },
  { value: "floors", label: "Floors", icon: "layers" },
  { value: "range", label: "Unit range", icon: "arrow-right" },
  { value: "units", label: "Specific units", icon: "check-square" },
];

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Who a notice goes to. Scope (this building / whole company) for the company login, then a
 * mode (everyone, floors, a unit range, specific units), the occupants toggle, and what that
 * resolves to right now: counts, and a list that can be narrowed to the people with no email.
 */
const AudiencePicker = ({
  audience,
  onChange,
  options,
  preview,
  loading,
  scope,
  canCompanyWide,
  onScopeChange,
  properties,
  selectedPropertyIds,
  onPropertyIdsChange,
  buildingLabel,
  disabled,
}) => {
  const [unitQuery, setUnitQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [listTab, setListTab] = useState("all"); // all | missing

  const set = (patch) => onChange({ ...audience, ...patch });
  const floors = options?.floors || [];
  const units = useMemo(() => options?.units || [], [options]);
  const filteredUnits = useMemo(() => {
    const q = unitQuery.trim().toLowerCase();
    return q ? units.filter((u) => String(u).toLowerCase().includes(q)) : units;
  }, [units, unitQuery]);

  const toggleIn = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const summary = preview?.summary || null;
  const recipients = preview?.recipients || [];
  const missing = recipients.filter((r) => !r.email);
  const listed = listTab === "missing" ? missing : recipients;
  const companyScope = scope === "company";

  return (
    <div className="rn-audience">
      {canCompanyWide && (
        <div className="rn-scope" role="group" aria-label="Scope">
          <button type="button" className={`rn-seg${!companyScope ? " on" : ""}`} onClick={() => onScopeChange("property")} disabled={disabled}>
            <FeatherIcon icon="home" size={13} /> {buildingLabel}
          </button>
          <button type="button" className={`rn-seg${companyScope ? " on" : ""}`} onClick={() => onScopeChange("company")} disabled={disabled}>
            <FeatherIcon icon="globe" size={13} /> Whole company
          </button>
        </div>
      )}

      {companyScope ? (
        <div className="rn-sub">
          <label className="rn-check rn-check-strong">
            <input type="checkbox" checked={selectedPropertyIds.length === 0} onChange={() => onPropertyIdsChange([])} disabled={disabled} />
            <span>All properties{properties.length ? ` (${properties.length})` : ""}</span>
          </label>
          <div className="rn-props-list">
            {properties.map((p) => (
              <label key={p.property_id} className={`rn-check${selectedPropertyIds.length === 0 ? " is-muted" : ""}`}>
                <input type="checkbox" checked={selectedPropertyIds.length === 0 || selectedPropertyIds.includes(p.property_id)} onChange={() => onPropertyIdsChange(toggleIn(selectedPropertyIds, p.property_id))} disabled={disabled} />
                <span>
                  {p.title}
                  {p.buildings?.length > 1 ? <small>{plural(p.buildings.length, "building")}</small> : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="rn-modes" role="group" aria-label="Who">
            {MODES.map((m) => (
              <button key={m.value} type="button" className={`rn-chip${audience.mode === m.value ? " on" : ""}`} aria-pressed={audience.mode === m.value} onClick={() => set({ mode: m.value })} disabled={disabled}>
                <FeatherIcon icon={m.icon} size={13} /> {m.label}
              </button>
            ))}
          </div>

          {audience.mode === "floors" && (
            <div className="rn-sub">
              {floors.length === 0 ? (
                <p className="rn-muted">No floors could be read from the unit numbers here. Use a unit range or specific units instead.</p>
              ) : (
                <div className="rn-floors">
                  {floors.map((f) => {
                    const on = audience.floors.includes(f.floor);
                    return (
                      <button key={f.floor} type="button" className={`rn-floor${on ? " on" : ""}`} aria-pressed={on} onClick={() => set({ floors: toggleIn(audience.floors, f.floor) })} disabled={disabled}>
                        <span className="t">Floor {f.floor}</span>
                        <span className="b">{plural(f.units, "unit")} · {plural(f.residents, "resident")}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {audience.mode === "range" && (
            <div className="rn-sub rn-range">
              <label>
                <span>From unit</span>
                <input type="text" list="rn-unit-list" value={audience.range.from} placeholder="e.g. 201" onChange={(e) => set({ range: { ...audience.range, from: e.target.value } })} disabled={disabled} />
              </label>
              <span className="rn-range-to">to</span>
              <label>
                <span>To unit</span>
                <input type="text" list="rn-unit-list" value={audience.range.to} placeholder="e.g. 210" onChange={(e) => set({ range: { ...audience.range, to: e.target.value } })} disabled={disabled} />
              </label>
              <datalist id="rn-unit-list">
                {units.map((u) => (
                  <option key={u} value={u} />
                ))}
              </datalist>
            </div>
          )}

          {audience.mode === "units" && (
            <div className="rn-sub">
              <div className="rn-unit-search">
                <FeatherIcon icon="search" size={13} />
                <input type="text" value={unitQuery} placeholder="Find a unit" onChange={(e) => setUnitQuery(e.target.value)} aria-label="Find a unit" disabled={disabled} />
                {audience.units.length > 0 && (
                  <button type="button" className="rn-link" onClick={() => set({ units: [] })} disabled={disabled}>
                    Clear {audience.units.length}
                  </button>
                )}
              </div>
              <div className="rn-units" role="group" aria-label="Units">
                {filteredUnits.map((u) => {
                  const on = audience.units.includes(u);
                  return (
                    <button key={u} type="button" className={`rn-unit${on ? " on" : ""}`} aria-pressed={on} onClick={() => set({ units: toggleIn(audience.units, u) })} disabled={disabled}>
                      {u}
                    </button>
                  );
                })}
                {filteredUnits.length === 0 && <span className="rn-muted">No unit matches.</span>}
              </div>
            </div>
          )}
        </>
      )}

      <label className="rn-check rn-occupants">
        <input type="checkbox" checked={!!audience.include_occupants} onChange={(e) => set({ include_occupants: e.target.checked })} disabled={disabled} />
        <span>
          Include occupants
          <small>Lease holders always get it; this adds the other people listed in each unit.</small>
        </span>
      </label>

      {/* ---- what it resolves to ---- */}
      <div className="rn-summary">
        {loading && !summary ? (
          <span className="rn-muted">
            <Spinner size="sm" /> Counting residents…
          </span>
        ) : summary ? (
          <>
            <span className="rn-summary-main" data-testid="rn-summary-main">
              <strong>{summary.total}</strong> resident{summary.total === 1 ? "" : "s"} · <strong>{summary.units}</strong> unit{summary.units === 1 ? "" : "s"}
              <span className="rn-summary-where"> · {companyScope ? (selectedPropertyIds.length ? plural(selectedPropertyIds.length, "property") : "whole company") : describeAudience(audience)}</span>
              {loading ? <Spinner size="sm" className="rn-summary-spin" /> : null}
            </span>
            {summary.without_email > 0 && (
              <button
                type="button"
                className="rn-chip-warn"
                onClick={() => {
                  setListTab("missing");
                  setListOpen(true);
                }}
                title="Show who has no email"
              >
                <FeatherIcon icon="alert-circle" size={12} /> {summary.without_email} without email
              </button>
            )}
            {recipients.length > 0 && (
              <button type="button" className="rn-link" onClick={() => setListOpen((o) => !o)}>
                {listOpen ? "Hide list" : "Show list"}
              </button>
            )}
          </>
        ) : (
          <span className="rn-muted">Pick a property to count residents.</span>
        )}
      </div>

      {listOpen && recipients.length > 0 && (
        <div className="rn-people-panel">
          <div className="rn-people-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={listTab === "all"} className={listTab === "all" ? "on" : ""} onClick={() => setListTab("all")}>
              All ({recipients.length})
            </button>
            <button type="button" role="tab" aria-selected={listTab === "missing"} className={listTab === "missing" ? "on" : ""} onClick={() => setListTab("missing")} disabled={missing.length === 0}>
              No email ({missing.length})
            </button>
          </div>
          <div className="rn-people">
            {listed.map((r) => (
              <div key={r.recipient_id} className={`rn-person${r.email ? "" : " is-missing"}`}>
                <span className="u">{r.unit_no}</span>
                <span className="n">
                  {r.name}
                  {r.role === "occupant" ? <em>occupant</em> : null}
                  {companyScope && r.property_name ? <small>{r.property_name}</small> : null}
                </span>
                <span className="e">{r.email || "no email"}</span>
              </div>
            ))}
          </div>
          {listTab === "missing" && missing.length > 0 && (
            <p className="rn-muted rn-people-note">Add their email in the directory table and they are included; otherwise they are listed as missed after sending.</p>
          )}
        </div>
      )}
    </div>
  );
};

export { AudiencePicker };
